import {
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import {
  configFileAtom,
  editedConfigAtom,
  isBusyAtom,
  partnerIngestionConfigAtom,
  selectedSourceOptionAtom,
  sourceDataAtom,
  userMessageAtom,
} from './atoms'
import { partnerIdAtom } from '../../components/ingestion/atoms'
import { uploadPartnerFile } from '../../components/ingestion/Utility'
import { jsonFile } from '../../utility'
import { useCallback, useMemo } from 'react'
import {
  AttributeConfig,
  IngestionConfig,
  IngestionConfigItem,
  NormalizedIngestionSource,
} from './types'
import deepEqual from 'fast-deep-equal'
import { titleForSourceData } from './configBasedIngestion'
import { googleUserAtom } from 'infrastructure/googleDrive'

export function useTransformSourceSelection() {
  const setIsBusy = useSetAtom(isBusyAtom)
  const setSourceData = useSetAtom(sourceDataAtom)
  const partnerConfig = useAtomValue(partnerIngestionConfigAtom)
  const selectedOption = useAtomValue(selectedSourceOptionAtom)
  const [gAuth, resetG] = useAtom(googleUserAtom)
  const setUserMessage = useSetAtom(userMessageAtom)

  return useCallback(async () => {
    if (!selectedOption) return
    setIsBusy(true)
    const { initialTransform } = partnerConfig
    try {
      const transformed = await initialTransform(
        selectedOption,
        gAuth,
        setUserMessage,
      )
      setSourceData(transformed || undefined)
    } catch {
      resetG()
    } finally {
      setIsBusy(false)
    }
  }, [
    partnerConfig,
    selectedOption,
    setSourceData,
    gAuth,
    setIsBusy,
    setUserMessage,
  ])
}

export function useSaveEditedConfig() {
  const editedConfig = useAtomValue(editedConfigAtom)
  const partnerKey = useAtomValue(partnerIdAtom)
  const resetConfig = useSetAtom(configFileAtom)

  return useCallback(async () => {
    if (!partnerKey || !editedConfig) return

    await uploadPartnerFile(
      partnerKey,
      jsonFile(editedConfig, 'ingestionConfig.json'),
    )
    resetConfig()
  }, [editedConfig, partnerKey])
}

export function useAttributesForTopLevelKey(configKey: string) {
  const editedConfig = useAtomValue(editedConfigAtom)
  return editedConfig?.[configKey]?.attributes
}

export function useSetTopLevelValue<T>(
  configKey: string,
  valueKey: keyof IngestionConfigItem,
) {
  const setEditingConfig = useSetAtom(editedConfigAtom)
  return useCallback(
    (newValFunc: (prev: T) => unknown) => {
      setEditingConfig((prev) => {
        const oldValue = prev![configKey][valueKey] as T
        const newValue = newValFunc(oldValue)
        return {
          ...prev!,
          [configKey]: {
            ...prev![configKey],
            [valueKey]: newValue,
          },
        } as IngestionConfig
      })
    },
    [setEditingConfig, configKey],
  )
}

export function useSetAttributeForTopLevelKey(configKey: string) {
  const attrSetter = useSetTopLevelValue<AttributeConfig>(
    configKey,
    'attributes',
  )
  return useCallback(
    (p: {
      attrKey: string
      attrProp: string
      newValue: string | number | boolean
    }) => {
      attrSetter((prevAttrs: AttributeConfig) => {
        return {
          ...prevAttrs,
          [p.attrKey]: {
            ...prevAttrs[p.attrKey],
            [p.attrProp]: p.newValue,
          },
        }
      })
    },
    [attrSetter, configKey],
  )
}

export function useSortedAttributes(configKey: string) {
  const editingConfig = useAtomValue(editedConfigAtom)
  if (!editingConfig) return
  const { attributes, attrIdOrder } = editingConfig[configKey]
  return Object.keys(attributes).reduce(
    (acc, key) => {
      if (!acc.includes(key)) {
        acc.push(key)
      }
      return acc
    },
    [...(attrIdOrder ?? [])],
  )
}

export function useSetSortOrder(configKey: string) {
  const setEditingConfig = useSetAtom(editedConfigAtom)
  return useCallback(
    (attrIdOrder: string[]) => {
      setEditingConfig((prev) => {
        return {
          ...prev,
          [configKey]: {
            ...prev![configKey],
            attrIdOrder,
          },
        }
      })
    },
    [configKey, setEditingConfig],
  )
}

export function useNodesOfTypeFromSource(configKey: string) {
  const config = useAtomValue(editedConfigAtom)
  const sourceData = useAtomValue(sourceDataAtom)
  if (!sourceData) return []

  const nodesOfType: { title: string; data: NormalizedIngestionSource }[] = []

  const doItem = (item: NormalizedIngestionSource, title: string[]) => {
    if (item.type === configKey) {
      nodesOfType.push({ title: title.join(': '), data: item })
    } else {
      item.children?.forEach((child, i) => {
        const childConfig = config?.[child.type]
        doItem(child, [
          ...title,
          titleForSourceData(child, childConfig?.customNodeTitle, i),
        ])
      })
    }
  }

  doItem(sourceData, [])
  return nodesOfType
}

export function useHasChanges() {
  const editedConfig = useAtomValue(editedConfigAtom)
  const sourceConfig = useAtomValue(configFileAtom)

  return useMemo(() => {
    return {
      configKeyHasChanges: (configKey: string) => {
        return !deepEqual(editedConfig?.[configKey], sourceConfig[configKey])
      },
      attrHasChanges: (configKey: string, attrKey: string) => {
        const edited = editedConfig?.[configKey]?.attributes[attrKey]
        const source = sourceConfig?.[configKey]?.attributes[attrKey]
        return !deepEqual(edited, source)
      },
      topLevelHasChanges: (
        configKey: string,
        topLevelKey: keyof IngestionConfigItem,
      ) => {
        const edited = editedConfig?.[configKey]?.[topLevelKey]
        const source = sourceConfig?.[configKey]?.[topLevelKey]
        return !deepEqual(edited, source)
      },
    }
  }, [editedConfig, sourceConfig])
}
